import { useState, useEffect, useMemo, useRef } from 'react';
import {
  View,
  Modal,
  Pressable,
  ScrollView,
  Switch,
  Alert,
  KeyboardAvoidingView,
  Platform,
  InteractionManager,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { SelectField } from '@/components/ui/SelectField';
import { SelectFieldModalProvider } from '@/components/ui/SelectFieldModalContext';
import { Calendar } from '@/components/ui/Calendar';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { updateTrip, type Trip, type UpdateTripData, type TripDestination } from '@/services/trips';
import { MultiCityDestinationForm } from './MultiCityDestinationForm';
import type { TravelMode } from '@/services/navigation';
import { AutocompleteInput } from '@/components/ui/AutocompleteInput';
import { getTripNameSuggestions } from '@/utils/formSuggestions';
import { CityAutocomplete } from '@/components/ui/CityAutocomplete';
import { DESTINATION_OPTIONS, findDestinationLabel } from '@/data/destinationOptions';
import { toIso2CountryCode } from '@/utils/countryIso2';
import { useToast } from '@/components/ui/ToastProvider';

type EditTripFormProps = {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  trip: Trip;
  userId: string;
};

const TRAVEL_MODE_OPTIONS = [
  { value: 'plane', label: '✈️ Plane' },
  { value: 'car', label: '🚗 Car' },
  { value: 'train', label: '🚂 Train' },
  { value: 'bus', label: '🚌 Bus' },
  { value: 'boat', label: '⛵ Boat' },
  { value: 'mixed', label: '🔀 Mixed' },
];

const PREFERRED_TRAVEL_MODE_OPTIONS: Array<{ value: TravelMode; label: string; description: string }> = [
  { value: 'driving', label: '🚗 Driving', description: 'Best for longer distances and flexibility' },
  { value: 'walking', label: '🚶 Walking', description: 'Great for exploring cities on foot' },
  { value: 'transit', label: '🚇 Public Transit', description: 'Efficient for urban travel' },
];

// Destination options are shared via `src/data/destinationOptions.ts`

export function EditTripForm({ visible, onClose, onSuccess, trip, userId }: EditTripFormProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { showSuccess } = useToast();
  const scrollRef = useRef<ScrollView>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [tripName, setTripName] = useState(trip.name || '');
  const [isMultiCity, setIsMultiCity] = useState(
    Array.isArray(trip.destinations) && trip.destinations.length > 1,
  );
  const [selectedDestination, setSelectedDestination] = useState('');
  const [customDestination, setCustomDestination] = useState('');
  const [destinations, setDestinations] = useState<TripDestination[]>(trip.destinations || []);
  const [startDate, setStartDate] = useState<Date | null>(trip.startDate || null);
  const [endDate, setEndDate] = useState<Date | null>(trip.endDate || null);
  const [travelMode, setTravelMode] = useState(trip.travelMode || '');
  const [preferredTravelMode, setPreferredTravelMode] = useState<TravelMode | undefined>(trip.preferredTravelMode || 'driving');
  const [emailSyncEnabled, setEmailSyncEnabled] = useState(trip.emailSyncEnabled || false);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);

  const multiCityDestinationsProp = useMemo(() => {
    if (destinations.length > 0) return destinations;
    if (startDate && endDate) {
      return [{ city: trip.destination || '', startDate, endDate }];
    }
    return [];
  }, [destinations, startDate?.getTime(), endDate?.getTime(), trip.destination]);

  // Initialize destination when trip changes
  useEffect(() => {
    if (trip.destination) {
      // Try to find matching option
      const matchingOption = DESTINATION_OPTIONS.find(
        opt => opt.label.toLowerCase() === (trip.destination ?? '').toLowerCase()
      );
      
      if (matchingOption) {
        setSelectedDestination(matchingOption.value);
        setCustomDestination('');
      } else {
        // Custom destination
        setSelectedDestination('other');
        setCustomDestination(trip.destination);
      }
    }
  }, [trip.destination]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!tripName.trim()) {
      newErrors.tripName = 'Trip name is required';
    }

    // Validate destination(s)
    if (isMultiCity) {
      if (destinations.length === 0) {
        newErrors.destinations = 'At least one destination is required';
      } else {
        let hasDestinationIssues = false;
        destinations.forEach((dest, index) => {
          if (!dest.city.trim()) {
            newErrors[`destination_${index}`] = `City ${index + 1} is required`;
            hasDestinationIssues = true;
          }
          if (dest.startDate >= dest.endDate) {
            newErrors[`destination_dates_${index}`] = `City ${index + 1}: End date must be after start date`;
            hasDestinationIssues = true;
          }
        });
        // Also set a top-level error so users always see why Save didn't work.
        if (hasDestinationIssues && !newErrors.destinations) {
          newErrors.destinations = 'Please complete all destinations (city + valid dates) before saving';
        }
      }
      // For multi-city trips, derive trip dates from destinations
      if (destinations.length > 0) {
        const earliestStart = destinations.reduce((earliest, dest) => 
          !earliest || dest.startDate < earliest ? dest.startDate : earliest, 
          null as Date | null
        );
        const latestEnd = destinations.reduce((latest, dest) => 
          !latest || dest.endDate > latest ? dest.endDate : latest, 
          null as Date | null
        );
        if (earliestStart) setStartDate(earliestStart);
        if (latestEnd) setEndDate(latestEnd);
      }
    } else {
      // For CityAutocomplete, check if we have a value
      const destinationValue =
        selectedDestination === 'other'
          ? customDestination
          : (findDestinationLabel(selectedDestination) || '');
      if (!destinationValue || !destinationValue.trim()) {
        newErrors.destination = 'Destination is required';
      } else if (selectedDestination === 'other' && !customDestination.trim()) {
        newErrors.customDestination = 'Please enter a custom destination';
      } else {
        // Require a destination that resolves to a country ISO2 (e.g. "Tokyo, Japan").
        const inferredCountry = destinationValue.split(',').map((p) => p.trim()).pop() || destinationValue;
        const iso2 = toIso2CountryCode(inferredCountry);
        if (!iso2) {
          newErrors.destination =
            'Please select a destination that includes the country (e.g., "Tokyo, Japan").';
        }
      }
      
      // For single-city trips, validate trip-level dates
      if (!startDate) {
        newErrors.startDate = 'Start date is required';
      }

      if (!endDate) {
        newErrors.endDate = 'End date is required';
      } else if (startDate && endDate < startDate) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    // Travel mode is optional for edits (older/imported trips may not have this set).

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      // Make validation failures obvious (especially when multi-city errors are above the fold)
      scrollRef.current?.scrollTo({ y: 0, animated: true });
      return;
    }

    setLoading(true);
    try {
      const updateData: UpdateTripData = {
        name: tripName.trim(),
        preferredTravelMode,
        emailSyncEnabled,
      };

      // For multi-city edits, derive trip-level dates from destinations (source of truth).
      // This allows picking "future" destination dates even if the old trip end date was earlier.
      if (isMultiCity) {
        const starts = destinations.map((d) => d.startDate).filter(Boolean);
        const ends = destinations.map((d) => d.endDate).filter(Boolean);
        const derivedStart = starts.length ? new Date(Math.min(...starts.map((d) => d.getTime()))) : startDate;
        const derivedEnd = ends.length ? new Date(Math.max(...ends.map((d) => d.getTime()))) : endDate;
        if (derivedStart) updateData.startDate = derivedStart;
        if (derivedEnd) updateData.endDate = derivedEnd;
      } else {
        updateData.startDate = startDate!;
        updateData.endDate = endDate!;
        // Keep nested destination dates aligned for all single-city edits (including legacy rows).
        if (startDate && endDate) {
          const primaryCity =
            trip.destinations?.[0]?.city ||
            (trip.destination || trip.name || 'Trip').split(',')[0]?.trim() ||
            'Trip';
          const primaryDest = trip.destinations?.[0];
          updateData.destinations = [
            {
              ...(primaryDest ?? {}),
              city: primaryCity,
              startDate,
              endDate,
            },
          ];
        }
      }

      // Only persist travelMode if user selected one (avoid writing empty string)
      if (travelMode && travelMode.trim().length > 0) {
        updateData.travelMode = travelMode;
      }

      if (isMultiCity) {
        updateData.destinations = destinations;
      } else {
        // Determine final destination value from CityAutocomplete
        const destinationValue =
          selectedDestination === 'other'
            ? customDestination
            : (findDestinationLabel(selectedDestination) || '');
        updateData.destination = destinationValue.trim();
      }

      const result = await updateTrip(trip.id, userId, updateData);

      onSuccess();
      if (result === 'server') {
        showSuccess('Changes saved');
      } else {
        showSuccess(
          "You're offline or the connection dropped briefly. Changes are saved on this device and will sync automatically.",
        );
      }
      InteractionManager.runAfterInteractions(() => {
        onClose();
      });
    } catch (error) {
      console.error('Error updating trip:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update trip. Please try again.';
      Alert.alert('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset to original values
    setTripName(trip.name || '');
    setSelectedDestination('');
    setCustomDestination('');
    setStartDate(trip.startDate || null);
    setEndDate(trip.endDate || null);
    setTravelMode(trip.travelMode || '');
    setPreferredTravelMode(trip.preferredTravelMode || 'driving');
    setEmailSyncEnabled(trip.emailSyncEnabled || false);
    setErrors({});
    setShowStartCalendar(false);
    setShowEndCalendar(false);
    onClose();
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleDestinationChange = (value: string) => {
    setSelectedDestination(value);
    if (value !== 'other') {
      setCustomDestination('');
      setErrors({ ...errors, customDestination: '' });
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={() => {
        if (loading) return;
        handleClose();
      }}
      // iOS can still render "page sheet" chrome depending on navigation context.
      // Use an overFullScreen transparent modal and paint our own full-screen surface.
      presentationStyle={Platform.OS === 'ios' ? 'overFullScreen' : 'fullScreen'}
      transparent
      accessibilityViewIsModal={true}
      accessibilityLabel="Edit trip"
    >
      <SelectFieldModalProvider>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
        <View
          style={{
            flex: 1,
            backgroundColor: theme.colors.surface,
            paddingTop: insets.top,
          }}
        >
          <View
            accessibilityLabel="Edit trip form"
            style={{
              backgroundColor: theme.colors.surface,
              flex: 1,
              paddingBottom: insets.bottom + theme.spacing(2),
              paddingTop: theme.spacing(1.5),
            }}
          >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: theme.spacing(2),
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderMuted,
            }}
          >
            <SubHeading>Edit trip</SubHeading>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close form"
              accessibilityHint="Closes the form and discards any unsaved changes"
              accessibilityState={{ disabled: loading }}
              disabled={loading}
              onPress={() => {
                if (loading) return;
                handleClose();
              }}
              style={({ pressed }) => ({
                opacity: loading ? 0.35 : pressed ? 0.7 : 1,
              })}
            >
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          </View>

          <ScrollView
            ref={scrollRef}
            style={{ flex: 1 }}
            contentContainerStyle={{
              padding: theme.spacing(2),
              paddingTop: theme.spacing(1),
              gap: theme.spacing(2),
              paddingBottom: theme.spacing(2) + insets.bottom,
            }}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
          >
            {/* Error Summary */}
            {Object.keys(errors).length > 0 && (
              <View
                accessibilityRole="alert"
                accessibilityLiveRegion="assertive"
                style={{
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.error + '15',
                  borderRadius: theme.radii.md,
                  borderWidth: 1,
                  borderColor: theme.colors.error + '40',
                  gap: theme.spacing(0.5),
                }}
              >
                <ThemedText variant="bodyStrong" color={theme.colors.error}>
                  Please fix the following errors:
                </ThemedText>
                {Object.entries(errors).map(([field, message]) => {
                  const fieldLabels: Record<string, string> = {
                    tripName: 'Trip name',
                    destination: 'Destination',
                    customDestination: 'Custom destination',
                    destinations: 'Destinations',
                    startDate: 'Start date',
                    endDate: 'End date',
                    travelMode: 'Travel mode',
                  };
                  // Improve labels for dynamic multi-city error keys
                  if (field.startsWith('destination_')) {
                    const index = Number(field.replace('destination_', ''));
                    fieldLabels[field] = Number.isFinite(index) ? `City ${index + 1}` : 'City';
                  }
                  if (field.startsWith('destination_dates_')) {
                    const index = Number(field.replace('destination_dates_', ''));
                    fieldLabels[field] = Number.isFinite(index) ? `City ${index + 1} dates` : 'City dates';
                  }
                  return (
                    <ThemedText key={field} variant="body" color={theme.colors.error}>
                      • {fieldLabels[field] || field}: {message}
                    </ThemedText>
                  );
                })}
              </View>
            )}

            <Card variant="outlined">
              <AutocompleteInput
                label="Trip name"
                required
                placeholder="e.g., Summer Vacation"
                value={tripName}
                onChangeText={(text) => {
                  setTripName(text);
                  if (errors.tripName) {
                    setErrors(prev => ({ ...prev, tripName: '' }));
                  }
                }}
                errorText={errors.tripName}
                autoCapitalize="words"
                suggestions={getTripNameSuggestions}
                minQueryLength={1}
                showSuggestionsOnFocus={true}
              />
            </Card>

            <Card variant="outlined">
              <View style={{ gap: theme.spacing(1.5) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <ThemedText variant="bodyStrong">Destination</ThemedText>
                  <Pressable
                    onPress={() => {
                      setIsMultiCity(!isMultiCity);
                      if (!isMultiCity && destinations.length === 0 && startDate && endDate) {
                        // Initialize with existing destination or single destination
                        if (trip.destination) {
                          setDestinations([{
                            city: trip.destination,
                            startDate: startDate,
                            endDate: endDate,
                          }]);
                        } else {
                          setDestinations([{
                            city: '',
                            startDate: startDate,
                            endDate: endDate,
                          }]);
                        }
                      }
                    }}
                    style={({ pressed }) => ({
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: theme.spacing(0.5),
                      padding: theme.spacing(0.5),
                      opacity: pressed ? 0.7 : 1,
                    })}
                  >
                    <Ionicons 
                      name={isMultiCity ? "checkbox" : "square-outline"} 
                      size={20} 
                      color={theme.colors.primary} 
                    />
                    <ThemedText variant="body" color={theme.colors.primary} style={{ fontSize: 14 }}>
                      Multi-city trip
                    </ThemedText>
                  </Pressable>
                </View>

                {isMultiCity ? (
                  <MultiCityDestinationForm
                    destinations={multiCityDestinationsProp}
                    tripStartDate={startDate || trip.startDate || new Date(0)} // Use epoch date if not set
                    tripEndDate={endDate || trip.endDate || new Date(0)} // Use epoch date if not set
                    onChange={setDestinations}
                    constrainToTripDates={false}
                  />
                ) : (
                  <>
                    <CityAutocomplete
                      label="City / Destination"
                      placeholder="Search or enter a destination"
                      value={
                        selectedDestination === 'other'
                          ? customDestination
                          : (findDestinationLabel(selectedDestination) || '')
                      }
                      onChangeText={(text) => {
                        // Check if it matches any predefined destination
                        const matchingOption = DESTINATION_OPTIONS.find(opt => 
                          opt.label.toLowerCase() === text.toLowerCase()
                        );
                        if (matchingOption) {
                          setSelectedDestination(matchingOption.value);
                          setCustomDestination('');
                        } else {
                          setSelectedDestination('other');
                          setCustomDestination(text);
                        }
                        if (errors.destination || errors.customDestination) {
                          setErrors(prev => ({ ...prev, destination: '', customDestination: '' }));
                        }
                      }}
                      onSelectCity={(suggestion) => {
                        // Check if the selected city matches any predefined destination
                        const matchingOption = DESTINATION_OPTIONS.find(opt => 
                          opt.label.toLowerCase().includes(suggestion.city.toLowerCase()) ||
                          suggestion.fullName.toLowerCase().includes(opt.label.toLowerCase())
                        );
                        if (matchingOption) {
                          setSelectedDestination(matchingOption.value);
                          setCustomDestination('');
                        } else {
                          setSelectedDestination('other');
                          setCustomDestination(suggestion.fullName);
                        }
                      }}
                      allowUseAsEntered
                    />
                    <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                      Tip: include the country (e.g., “Tokyo, Japan”) so we can check visa requirements accurately.
                    </Caption>
                    {errors.destination && (
                      <Caption color={theme.colors.error} style={{ marginTop: theme.spacing(0.5) }}>
                        {errors.destination}
                      </Caption>
                    )}
                    {errors.customDestination && (
                      <Caption color={theme.colors.error} style={{ marginTop: theme.spacing(0.5) }}>
                        {errors.customDestination}
                      </Caption>
                    )}
                  </>
                )}
                {errors.destinations && (
                  <Caption color={theme.colors.error}>{errors.destinations}</Caption>
                )}
              </View>
            </Card>

            {/* Only show trip-level dates for single-city trips */}
            {!isMultiCity && (
              <>
                <Card variant="outlined">
                  <View style={{ gap: theme.spacing(0.75) }}>
                    <ThemedText variant="bodyStrong" color={errors.startDate ? theme.colors.error : theme.colors.textSecondary}>
                      Start date
                    </ThemedText>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Start date, ${startDate ? formatDate(startDate) : 'not set'}. Tap to change date`}
                      accessibilityHint="Opens calendar picker to select trip start date. Required field."
                      accessibilityRequired={true}
                      onPress={() => {
                        setShowStartCalendar(true);
                        if (errors.startDate) {
                          setErrors(prev => ({ ...prev, startDate: '' }));
                        }
                      }}
                      style={({ pressed }) => [
                        {
                          borderWidth: 1,
                          borderColor: errors.startDate
                            ? theme.colors.error
                            : theme.colors.border,
                          borderRadius: theme.radii.md,
                          paddingHorizontal: theme.spacing(2),
                          paddingVertical: theme.spacing(1.5),
                          backgroundColor: theme.colors.surface,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          opacity: pressed ? 0.85 : 1,
                          minHeight: 44,
                        },
                      ]}
                    >
                      <ThemedText
                        variant="body"
                        color={startDate ? theme.colors.text : theme.colors.textSecondary}
                      >
                        {startDate ? formatDate(startDate) : 'Select start date'}
                      </ThemedText>
                      <Ionicons
                        name="calendar-outline"
                        size={20}
                        color={theme.colors.textSecondary}
                      />
                    </Pressable>
                    {errors.startDate && (
                      <Caption 
                        color={theme.colors.error}
                        accessibilityLiveRegion="polite"
                        accessibilityRole="alert"
                      >
                        {errors.startDate}
                      </Caption>
                    )}
                  </View>
                </Card>

                <Card variant="outlined">
                  <View style={{ gap: theme.spacing(0.75) }}>
                    <ThemedText variant="bodyStrong" color={errors.endDate ? theme.colors.error : theme.colors.textSecondary}>
                      End date
                    </ThemedText>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`End date, ${endDate ? formatDate(endDate) : 'not set'}. Tap to change date`}
                      accessibilityHint="Opens calendar picker to select trip end date. Required field."
                      accessibilityRequired={true}
                      onPress={() => {
                        setShowEndCalendar(true);
                        if (errors.endDate) {
                          setErrors(prev => ({ ...prev, endDate: '' }));
                        }
                      }}
                      style={({ pressed }) => [
                        {
                          borderWidth: 1,
                          borderColor: errors.endDate
                            ? theme.colors.error
                            : theme.colors.border,
                          borderRadius: theme.radii.md,
                          paddingHorizontal: theme.spacing(2),
                          paddingVertical: theme.spacing(1.5),
                          backgroundColor: theme.colors.surface,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          opacity: pressed ? 0.85 : 1,
                          minHeight: 44,
                        },
                      ]}
                    >
                      <ThemedText
                        variant="body"
                        color={endDate ? theme.colors.text : theme.colors.textSecondary}
                      >
                        {endDate ? formatDate(endDate) : 'Select end date'}
                      </ThemedText>
                      <Ionicons
                        name="calendar-outline"
                        size={20}
                        color={theme.colors.textSecondary}
                      />
                    </Pressable>
                    {errors.endDate && (
                      <Caption 
                        color={theme.colors.error}
                        accessibilityLiveRegion="polite"
                        accessibilityRole="alert"
                      >
                        {errors.endDate}
                      </Caption>
                    )}
                  </View>
                </Card>
              </>
            )}

            <Card variant="outlined">
              <SelectField
                label="Travel mode"
                placeholder="Select how you'll travel"
                value={travelMode}
                options={TRAVEL_MODE_OPTIONS}
                onValueChange={setTravelMode}
                errorText={errors.travelMode}
              />
            </Card>

            <Card variant="outlined">
              <View style={{ gap: theme.spacing(1) }}>
                <ThemedText variant="bodyStrong">Local Travel Preference</ThemedText>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                  How you'll get around during your trip (for itinerary scheduling)
                </Caption>
                <View style={{ gap: theme.spacing(1) }}>
                  {PREFERRED_TRAVEL_MODE_OPTIONS.map((option) => {
                    const isSelected = preferredTravelMode === option.value;
                    return (
                      <Pressable
                        key={option.value}
                        onPress={() => setPreferredTravelMode(option.value)}
                        style={({ pressed }) => [
                          {
                            flexDirection: 'row',
                            alignItems: 'center',
                            padding: theme.spacing(1.5),
                            borderRadius: theme.radii.md,
                            borderWidth: 1,
                            borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                            backgroundColor: isSelected ? theme.colors.primary + '10' : theme.colors.surface,
                            opacity: pressed ? 0.7 : 1,
                            gap: theme.spacing(1),
                          },
                        ]}
                      >
                        <View style={{ flex: 1 }}>
                          <ThemedText variant="bodyStrong" style={{ fontSize: 15 }}>
                            {option.label}
                          </ThemedText>
                          <Caption color={theme.colors.textSecondary} style={{ fontSize: 12, marginTop: theme.spacing(0.25) }}>
                            {option.description}
                          </Caption>
                        </View>
                        {isSelected && (
                          <Ionicons name="checkmark-circle" size={24} color={theme.colors.primary} />
                        )}
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            </Card>

            <Card variant="outlined">
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingVertical: theme.spacing(1),
                }}
              >
                <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                  <ThemedText variant="bodyStrong">Sync email reservations</ThemedText>
                  <Caption color={theme.colors.textSecondary}>
                    Automatically import bookings from your email
                  </Caption>
                </View>
                <Switch
                  value={emailSyncEnabled}
                  onValueChange={setEmailSyncEnabled}
                  trackColor={{ false: theme.colors.borderMuted, true: theme.colors.primary }}
                  thumbColor={theme.colors.surface}
                />
              </View>
            </Card>

            {errors.submit && (
              <Card variant="outlined">
                <Body color={theme.colors.error}>{errors.submit}</Body>
              </Card>
            )}

            <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), marginTop: theme.spacing(1) }}>
              <Button
                label="Cancel"
                variant="ghost"
                onPress={handleClose}
                disabled={loading}
                accessibilityLabel="Cancel and close form"
                accessibilityHint="Discards any changes and closes the form"
                style={{ flex: 1 }}
              />
              <Button
                label={loading ? 'Saving...' : 'Save changes'}
                onPress={handleSubmit}
                disabled={loading}
                accessibilityLabel={loading ? 'Saving changes, please wait' : 'Save changes'}
                accessibilityState={{ disabled: loading, busy: loading }}
                style={{ flex: 1 }}
              />
            </View>
          </ScrollView>
        </View>
      </View>

      {/* Calendar modals — inside flex root so iOS in-modal overlay covers the sheet */}
      <Calendar
        visible={showStartCalendar}
        onClose={() => setShowStartCalendar(false)}
        onSelectDate={(date) => {
          setStartDate(date);
          if (endDate && date > endDate) {
            setEndDate(null);
          }
        }}
        selectedDate={startDate}
        minDate={today}
        title="Select Start Date"
      />

      <Calendar
        visible={showEndCalendar}
        onClose={() => setShowEndCalendar(false)}
        onSelectDate={setEndDate}
        selectedDate={endDate}
        minDate={startDate || today}
        title="Select End Date"
      />
      </KeyboardAvoidingView>
      </SelectFieldModalProvider>
    </Modal>
  );
}

